import { useMsal } from "@azure/msal-react";
import React, { useEffect } from 'react';
import { ComponentsHelper } from "../../core/services/ComponentHelper";
import { IViewAsAccessRequest } from "../types/IViewAsAccessRequest";
import { DEFAULT_GUID } from "../../accounts/Consts";

export interface IMyCasesPageProps {
    location: any;
    powerPortalURL: string;
    currentUserGuid: string;
    currentViewAsUserName: string;
    getUserCRMValues: () => void;
    viewAsAccessRequests?: IViewAsAccessRequest[];
}

type MyCasesSessionPayload = {
    powerPortalURL?: string;
    currentUserGuid?: string;
    currentViewAsUserName?: string;
    aboutProps?: unknown;
};

const safeParseMyCasesProps = (): MyCasesSessionPayload | null => {
    try {
        return JSON.parse(sessionStorage.getItem('myCasesProps') || 'null');
    } catch {
        return null;
    }
};

const buildMyCasesPayload = (
    existing: MyCasesSessionPayload | null,
    props: IMyCasesPageProps
): MyCasesSessionPayload => {
    const payload: MyCasesSessionPayload = {
        ...(existing || {}),
        powerPortalURL: props.powerPortalURL,
        currentUserGuid: props.currentUserGuid,
        currentViewAsUserName: props.currentViewAsUserName,
    };

    if (props.location?.aboutProps !== undefined) {
        payload.aboutProps = props.location.aboutProps;
    }

    return payload;
};

const MyCasesPage = (props: IMyCasesPageProps) => {
    const { instance, accounts } = useMsal();
    const langMap: Record<string, string> = { "en-GB": "en-US", "fr": "fr-FR" };
    const storedPreferences = React.useMemo(() => {
        try {
            const raw = JSON.parse(localStorage.getItem('userLanguagePreferences') || '"en-US"');
            return langMap[raw] || raw;
        } catch {
            return 'en-US';
        }
    }, []);
    let { powerPortalURL, currentUserGuid, currentViewAsUserName, location } = props;
    let aboutProps = location?.aboutProps;

    const storedPayload = safeParseMyCasesProps();
    powerPortalURL ||= storedPayload?.powerPortalURL;
    currentUserGuid ||= storedPayload?.currentUserGuid;
    currentViewAsUserName ||= storedPayload?.currentViewAsUserName;
    aboutProps ??= storedPayload?.aboutProps;

    useEffect(() => {
        if (props.powerPortalURL) {
            const payload = buildMyCasesPayload(safeParseMyCasesProps(), props);
            sessionStorage.setItem('myCasesProps', JSON.stringify(payload));
        }
    }, [props.powerPortalURL, props.currentUserGuid, props.currentViewAsUserName, props.location?.aboutProps]);

    useEffect(() => {
        const account = accounts[0]
        ComponentsHelper.createBearerHeaderFromAADLogin(instance, account);
    }, [instance, accounts]);

    useEffect(() => {
        if (!currentUserGuid) {
            props.getUserCRMValues();
        }
    }, [currentUserGuid]);

    useEffect(() => {
        setTimeout(() => {
            window.scrollTo({
                top: 0,
                behavior: "smooth",
            });
        }, 0);
    }, []);

    const viewAsName =
      currentViewAsUserName ||
      props.viewAsAccessRequests?.find(
        (item) => item.defaultAccess === true && item.status === "Approved",
      )?.ipName;

    const { guid } = aboutProps || {};

    const frameSrc = (() => {
        const hasGuid = typeof guid === "string" ? guid.trim() !== "" : Boolean(guid);
        const accountGuid = currentUserGuid === DEFAULT_GUID ? "" : currentUserGuid;

        if (!hasGuid && accountGuid) {
            return `${powerPortalURL}/${storedPreferences}/support/create-case?accountId=${accountGuid}&name=${encodeURIComponent(viewAsName)}&lang=${storedPreferences}`;
        }
        if (hasGuid) {
            return `${powerPortalURL}/${storedPreferences}/support/edit-case/?id=${guid}&lang=${storedPreferences}`;
        }
        return `${powerPortalURL}/${storedPreferences}/support/create-case?lang=${storedPreferences}`;
    })();

    return (
        <iframe
            src={frameSrc}
            style={{ width: '100%', height: '100vh' }}
            title="My Cases"
        />
    );
}

export default MyCasesPage;